import { useEffect, useState } from 'react';

import { Button, Box, Typography } from '@mui/material';

import { useAppContext, useFacilityContext } from '@/context';
import { getBffAvailabilityByLocation } from '@/services/getBffAvailabilityByLocation';
import { BffBrandType } from '@/index';

type AccordionPAOProps = {
	type?: 'sick';
	title?: string;
};

type AppointmentType = {
	time: string;
	date: string;
};

function capitalizeFirstLetter(string: string | null | undefined): string {
	if (!string) return '';
	return string.charAt(0).toUpperCase() + string.slice(1);
}

function convertTimeTo12HourFormat(time: string): string {
	if (!time) return '';

	const [hourStr, minute] = time.split(':');
	let hour = Number.parseInt(hourStr, 10);
	const ampm = hour >= 12 ? 'PM' : 'AM';
	hour = hour % 12 || 12;

	return `${hour}:${minute} ${ampm}`;
}

const AccordionPAO: React.FC<AccordionPAOProps> = ({ type = 'sick', title = 'Sick pet' }: AccordionPAOProps) => {
	const { officeInfo } = useFacilityContext(true);
	const { environment, config, appName, appVersion } = useAppContext();
	const siteUrl = environment === 'prod' ? config.siteURL : config.siteURLStage;
	const url =
		environment === 'prod' ? config.services.prod.BEFFE_GRAPHQL_URL : config.services.nonprod.BEFFE_GRAPHQL_URL;
	const [appointment, setAppointment] = useState<AppointmentType | null>(null);
	const [scheduleText, setScheduleText] = useState('Now');

	useEffect(() => {
		if (!officeInfo) return;

		const fetchFirstAppointment = async () => {
			try {
				const facilityIdShort = officeInfo.code.replace(/^LV_/, '');
				const startDate = new Date();
				const endDate = new Date(startDate);
				endDate.setDate(startDate.getDate() + 8);

				const availabilityData = await getBffAvailabilityByLocation(
					'LOVET' as BffBrandType,
					facilityIdShort,
					url,
					appName,
					appVersion,
					startDate,
					endDate
				);

				if (availabilityData?.availabilityByLocation?.availableSlots) {
					const allSlots = availabilityData.availabilityByLocation.availableSlots;
					const filteredSlotsByType = allSlots.filter((slot) => slot?.type?.toLowerCase() === type);

					if (filteredSlotsByType.length === 0) {
						setAppointment(null);
						return;
					}

					const year = startDate.getFullYear();
					const month = String(startDate.getMonth() + 1).padStart(2, '0');
					const day = String(startDate.getDate()).padStart(2, '0');
					const appointment = filteredSlotsByType[0];
					setAppointment(appointment);
					if (appointment?.date === `${year}-${month}-${day}`) {
						setScheduleText('Now');
					} else {
						const [, monthStr, dayStr] = appointment.date.split('-');
						setScheduleText(`${monthStr}/${dayStr}`);
					}
				}
			} catch (error) {
				console.error('Error fetching times:', error);
				return;
			}
		};

		void fetchFirstAppointment();
	}, [officeInfo, appName, appVersion, type, url]);

	if (!officeInfo) {
		return null;
	}

	return (
		<Box sx={{ mt: 2 }}>
			<Typography component="p" variant="bodyMediumSemiBold" color="text.secondary" sx={{ mb: 0.5 }}>
				{title} exam appointments at{' '}
				<Typography variant="bodyMediumSemiBold" color="text.primary">
					{capitalizeFirstLetter(officeInfo?.brand?.toLowerCase())} - {officeInfo?.name}
				</Typography>
			</Typography>
			<Typography component="p" variant="bodySmallBook" sx={{ color: 'text.tertiary' }}>
				{officeInfo.address?.address1}
			</Typography>
			<Typography variant="bodySmallBook" sx={{ color: 'text.tertiary' }}>
				{officeInfo.address?.city}, {officeInfo.address?.stateCode} {officeInfo.address?.zipCode}
			</Typography>
			<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} mt={2}>
				{appointment ? (
					<>
						<Button
							size="M"
							variant="primaryDefault"
							data-testid="button_schedule_today"
							href={`${siteUrl}/book-an-appointment/?fc=${officeInfo.code}&visitReason=${type}&timeSlot=${appointment?.date}T${appointment?.time}`}
						>
							Schedule {scheduleText} at {convertTimeTo12HourFormat(appointment?.time)}
						</Button>
						<Button
							size="M"
							variant="secondaryDefault"
							data-testid="button_see_all_times"
							href={`${siteUrl}/book-an-appointment/?fc=${officeInfo.code}&visitReason=${type}`}
						>
							See all available times
						</Button>
					</>
				) : (
					<Button
						size="M"
						variant="primaryDefault"
						data-testid="button_schedule_today"
						href={`${siteUrl}/book-an-appointment/?fc=${officeInfo?.code}&visitReason=${type}`}
					>
						Schedule Now
					</Button>
				)}
			</Box>
		</Box>
	);
};

export default AccordionPAO;
